import React, { useEffect, useState } from 'react';
import { X, Play } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useTour } from '../../contexts/TourContext';
import { useApp } from '../../contexts/AppContext';

export const WelcomePrompt: React.FC = () => {
  const { hasSeenWelcomePrompt, setHasSeenWelcomePrompt, startTour, currentRoleId } = useTour();
  const { currentUser } = useApp();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!hasSeenWelcomePrompt) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasSeenWelcomePrompt]);

  const handleTakeTour = () => {
    setIsVisible(false);
    setHasSeenWelcomePrompt(true);
    startTour('dashboard-overview');
  };

  const handleSkip = () => {
    setIsVisible(false);
    setHasSeenWelcomePrompt(true);
  };

  const handleClose = () => {
    setIsVisible(false);
    setHasSeenWelcomePrompt(true);
  };

  if (!isVisible) return null;

  const firstName = currentUser.name.split(' ')[0];

  // Role-specific welcome messages keyed by role template ID
  const getWelcomeMessage = () => {
    switch (currentRoleId) {
      case 'executive':
        return {
          greeting: `Hi ${firstName}, let's get you started`,
          description: 'Take a quick 3-minute tour to see your 10-second check and key metrics.',
        };
      case 'cfo':
      case 'regional':
        return {
          greeting: `Hi ${firstName}, welcome to Access CFF`,
          description: 'Take a quick tour to learn how to review forecasts, analyse variance, and spot issues.',
        };
      case 'manager':
        return {
          greeting: `Hi ${firstName}, let's get you started`,
          description: 'Take a quick tour to learn how to review forecasts, create scenarios, and track accuracy.',
        };
      case 'preparer':
      case 'external':
        return {
          greeting: `Hi ${firstName}, welcome!`,
          description: 'Take a quick tour to learn how to manage transactions and ensure accurate forecasts.',
        };
      case 'system_admin':
        return {
          greeting: `Hi ${firstName}, welcome to Access CFF`,
          description: 'Take a quick tour to learn how to configure settings and manage user roles.',
        };
      default:
        return {
          greeting: 'Welcome to Access CFF!',
          description: 'Take a quick tour to learn the basics.',
        };
    }
  };

  const { greeting, description } = getWelcomeMessage();

  return (
    <div className="fixed inset-0 bg-black/50 z-[9990] flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-300">
        <CardContent className="pt-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">👋</span>
              <h3 className="text-xl font-semibold">{greeting}</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mt-1 -mr-1"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-muted-foreground mb-6">
            {description}
          </p>

          <div className="flex flex-col gap-2">
            <Button onClick={handleTakeTour} size="lg" className="w-full">
              <Play className="h-4 w-4 mr-2" />
              Start Tour
            </Button>
            <Button variant="outline" onClick={handleSkip} size="lg" className="w-full">
              Skip for Now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
